/**
 * jump-graph.ts — wire symmetric system-pair adjacency into a navigable
 * hyperlane net (GALAXY §3.5.1).
 *
 * v4M5.3b3c — rewritten for the continuous Jump Area model per
 * `HYPERSPACE.md §4`. Previous versions paired per-system JumpPoint
 * entities; with JPs retired, adjacency lives directly on each
 * `StarSystem.adjacentSystemIds`.
 *
 * Strategy:
 *   1. Build a connected graph using a Kruskal-style minimum spanning
 *      tree keyed on Euclidean distance between systems. Guarantees
 *      every system has at least one link.
 *   2. Add a small number of extra edges (the next-shortest candidates
 *      after the MST) for routing variety. Capped at ~30% of MST edge
 *      count.
 *   3. Each chosen edge writes the partner systemId into both
 *      adjacency lists (symmetric). Duplicate writes are guarded.
 *
 * Determinism: stable tie-breaking via `state.rng`. Re-running with the
 * same seed produces the same hyperlane topology.
 *
 * Shape-specific topologies (v4M2.2) — when `explicitEdges` is non-empty,
 * the MST+extras pass is skipped and the explicit list is wired
 * verbatim. Each `[i, j]` entry references systems by index in
 * `[...state.systems.values()]` order at call time.
 */

import { nextFloat } from '../state/rng';
import type { GameState, SystemId } from '../state/types';

/** Fraction of MST edges to add as extra cross-links for route variety. */
const EXTRA_EDGE_FRACTION = 0.3;

interface CandidateEdge {
  a: SystemId;
  b: SystemId;
  dist: number;
  /** RNG-drawn jitter so equal-distance ties resolve deterministically. */
  jitter: number;
}

/**
 * Wire system-pair adjacency across `state`. Idempotent — calling on
 * an already-wired graph is a no-op.
 *
 * When `explicitEdges` is non-empty, the shape-specific adjacency from
 * `generateGalaxyByShape` is used directly: each `[i, j]` entry
 * references systems by index in `[...state.systems.values()]` order
 * at call time. The MST + extra-edges path is skipped. When omitted or
 * empty, the legacy distance-based MST runs (Even shape + any caller
 * pre-dating v4M2.2).
 */
export function wireJumpGraph(
  state: GameState,
  explicitEdges?: ReadonlyArray<readonly [number, number]>,
): void {
  const systems = [...state.systems.values()];
  if (systems.length < 2) return;

  // Reset adjacency on every system so callers re-running this on a
  // partially-wired state get a clean re-wire rather than duplicates.
  for (const sys of systems) sys.adjacentSystemIds = [];

  // v4M2.2 — shape-specific adjacency path. Each [i, j] is a system-pair
  // link; written verbatim, no MST / extras pass.
  if (explicitEdges && explicitEdges.length > 0) {
    for (const [i, j] of explicitEdges) {
      const a = systems[i];
      const b = systems[j];
      if (!a || !b) continue;
      linkSystems(a.id, b.id, state);
    }
    return;
  }

  // Enumerate every system-pair as a candidate edge sorted by distance.
  const candidates: CandidateEdge[] = [];
  for (let i = 0; i < systems.length; i++) {
    for (let j = i + 1; j < systems.length; j++) {
      const a = systems[i]!;
      const b = systems[j]!;
      const dx = a.x - b.x;
      const dy = a.y - b.y;
      candidates.push({
        a: a.id,
        b: b.id,
        dist: Math.hypot(dx, dy),
        jitter: nextFloat(state.rng) * 1e-6,
      });
    }
  }
  candidates.sort((p, q) => p.dist + p.jitter - (q.dist + q.jitter));

  // Kruskal MST with Union-Find.
  const parent = new Map<SystemId, SystemId>();
  for (const sys of systems) parent.set(sys.id, sys.id);
  const find = (x: SystemId): SystemId => {
    let r = x;
    while (parent.get(r)! !== r) r = parent.get(r)!;
    let cur = x;
    while (parent.get(cur)! !== r) {
      const next = parent.get(cur)!;
      parent.set(cur, r);
      cur = next;
    }
    return r;
  };
  const union = (x: SystemId, y: SystemId): boolean => {
    const rx = find(x);
    const ry = find(y);
    if (rx === ry) return false;
    parent.set(rx, ry);
    return true;
  };

  const mstEdges: CandidateEdge[] = [];
  const usedEdgeIdx = new Set<number>();
  for (let idx = 0; idx < candidates.length && mstEdges.length < systems.length - 1; idx++) {
    const e = candidates[idx]!;
    if (union(e.a, e.b)) {
      mstEdges.push(e);
      usedEdgeIdx.add(idx);
    }
  }

  for (const e of mstEdges) linkSystems(e.a, e.b, state);

  // Optional cross-links for route variety.
  const extraTarget = Math.floor(mstEdges.length * EXTRA_EDGE_FRACTION);
  let extraAdded = 0;
  for (let idx = 0; idx < candidates.length && extraAdded < extraTarget; idx++) {
    if (usedEdgeIdx.has(idx)) continue;
    const e = candidates[idx]!;
    if (linkSystems(e.a, e.b, state)) extraAdded++;
  }
}

/**
 * Write the partner systemId into both systems' `adjacentSystemIds`
 * lists. Idempotent on duplicate writes. Returns false if either
 * system doesn't resolve; true on a successful new link.
 */
function linkSystems(a: SystemId, b: SystemId, state: GameState): boolean {
  const sysA = state.systems.get(a);
  const sysB = state.systems.get(b);
  if (!sysA || !sysB) return false;
  let didLink = false;
  if (!sysA.adjacentSystemIds.includes(b)) {
    sysA.adjacentSystemIds.push(b);
    didLink = true;
  }
  if (!sysB.adjacentSystemIds.includes(a)) {
    sysB.adjacentSystemIds.push(a);
    didLink = true;
  }
  return didLink;
}
